import React, { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericSearchbar, { ParsedFilter } from "@ui-components/Searchbar/GenericSearchbar";
import { reflectorColumns, Reflector as TableReflector } from "./ReflectorData";
import ClusterPage from "./Cluster/ClusterPage";
import { getReflectorsList } from "./request/request";
import { useAuth0 } from "@auth0/auth0-react";
import EmptyItems from "@shared/components/EmptyItems";
import { filterReflectors } from "@shared/components/utils/searchUtils";
import { mapAvailabilityToStatus } from "../../helpers/formattersShapedData";
import { useAppSelector } from "@shared/redux/hooks";

// Using centralized mapAvailabilityToStatus from formattersShapedData

const suggestedKeys = [
  "Reflector ID",
  "Name",
  "Status",
  "Cloud",
  "Region",
  "Cluster",
  "Version",
  "Last Sync",
  "UUID",
];

// Interface for API response
interface Reflector {
  id: number;
  name: string;
  description: string;
  available: number;
  availability: number | null;
  lastUpdate: string;
  licenseId: number;
  version: string;
  data: {
    ha: string;
    cni: Record<string, string>;
    cloud: string;
    ha_ip: string | null;
    region: string;
  };
  tenantId: number;
  subId: string;
  clusterId: number | null;
  localPref: number;
  lastUpdatePeer: string;
  statusPeer: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  cluster: {
    id: number;
    name: string;
    description: string;
    enable: boolean;
  } | null;
  tenant: {
    id: number;
    Name: string;
    nameDisplay: string;
  };
}

const ReflectorsPage: React.FC = () => {
  const location = useLocation();
  const isDeviceView = location.pathname === "/reflector-v2/management/bgp-reflectors";
  const isClusterView = location.pathname === "/reflector-v2/management/clusters";

	const { tenantId } = useAppSelector(state => state.selection);
  // ========== Auth0 para obtener token y llamar a nuestros endpoints ==========
  const { isAuthenticated, isLoading: isAuthLoading, getAccessTokenSilently } = useAuth0();

  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // API state
  const [reflectors, setReflectors] = useState<TableReflector[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (isAuthLoading || !isAuthenticated) return;
    const fetchReflectors = async () => {
      setIsFetching(true);
      try {
        const token = await getAccessTokenSilently();
        const response = await getReflectorsList(token, tenantId);
        const shapedData: TableReflector[] = response.data.map((item: Reflector) => ({
          id: item.id ? String(item.id) : "",
          name: item.name || "",
          subId: item.subId || "",
          status: mapAvailabilityToStatus(item.available),
          cloud: item.data?.cloud ? item.data.cloud.toUpperCase() : "",
          region: item.data?.region || "",
          cluster: item.cluster?.name || (item.clusterId ? String(item.clusterId) : "—"),
          version: item.version || "-",
          lastSync: item.lastUpdate || "-",
          description: item.description || "",
        }));
        setReflectors(shapedData);
        
        // Set initial rows per page based on total data count
        if (shapedData.length > 5) {
          setRowsPerPage(10);
        } else {
          setRowsPerPage(5);
        }
        
      } catch (error) {
        console.error("Error fetching reflectors:", error);
      } finally {
        setIsFetching(false);
      }
    };
    fetchReflectors();
  }, [isAuthenticated, isAuthLoading, getAccessTokenSilently]);


  // Filtering logic using utility function
  const filteredData = useMemo(() => {
    return filterReflectors(reflectors, activeFilters);
  }, [activeFilters, reflectors]);

  return (
    <div className="relative">
      <GenericCard isCollapsible={false}>
        {/* Device View */}
        {isDeviceView && (
          <>
            <div className="mb-2 p-4">
              <GenericSearchbar
                placeholder="Find by attribute or value (case-sensitive)"
                suggestedKeys={suggestedKeys}
                onFiltersChange={setActiveFilters}
              />
            </div>
            {/* Empty state if no reflectors and not loading */}
            {!isFetching && reflectors.length === 0 && <EmptyItems />}
            {/* Only show table if there is data */}
            {reflectors.length > 0 && (
              <GenericTable
                columns={reflectorColumns}
                data={filteredData}
                initialRowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10]}
                width="100%"
                tableClassName="bg-semantic-bg-primary"
              />
            )}
          </>
        )}
        {/* Cluster View */}
        {isClusterView && <ClusterPage />}
      </GenericCard>

			{/* Overlay semi-transparente sólo si isFetching */}
			{isFetching && (
				<div
					className="
            fixed
            inset-0
            flex flex-col items-center justify-center
            bg-black/50
            z-[9999]
          "
				>
					<h2 className="text-xl font-semibold mb-4 text-white">
						Processing request...
					</h2>
					<div
						className="
              animate-spin
              rounded-full
              h-12 w-12
              border-4
              border-gray-300
              border-t-blue-500
            "
					/>
				</div>
			)}
    </div>
  );
};

export default ReflectorsPage; 